import { Injectable } from '@angular/core';
import { SystemParametersDto, UpdateSystemParametersPayload } from '@nebula-shared/domain';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { BaseCrudService } from '../crud/base-crud.service';
import { ModuloParametros, ParametrosSistemaPort } from './parametros-sistema.port';

export interface SystemDateApiResponse {
  response: {
    content: {
      fechaSistema: string;
      fechaServidor?: string;
    };
  };
}

/** Fecha de operacion de cada modulo que publica la suya. */
const RUTA_FECHA: Record<ModuloParametros, string> = {
  accounting: '/nebula-accounting-core/api/v1/parametros-sistema/system-date',
  treasury: '/nebula-treasury/api/v1/parametros-tesoreria/system-date',
  invoicing: '/nebula-invoicing/api/v1/parametros-sistema/system-date',
};

/**
 * Fecha de operacion de CONTABILIDAD, que es el modulo por defecto.
 *
 * Es la fuente que usa la barra superior mientras la libreria del modulo en el
 * que se esta no haya registrado la suya, y la que se lee en los modulos que no
 * tienen parametros propios.
 */
@Injectable({ providedIn: 'root' })
export class ServiceSystemTopbarDate
  extends BaseCrudService<
    SystemParametersDto,
    Partial<SystemParametersDto>,
    UpdateSystemParametersPayload
  >
  implements ParametrosSistemaPort
{
  constructor() {
    super();
    this.setBaseUrl(RUTA_FECHA.accounting);
  }

  /** Fecha de contabilidad: la de por defecto (`ParametrosSistemaPort`). */
  getSystemDate(): Observable<{ fechaSistema: string | null }> {
    return this.consultar('accounting');
  }

  /** @deprecated Usar {@link getSystemDate}; se conserva por los llamadores actuales. */
  getParametros(): Observable<{ fechaSistema: string }> {
    return this.consultar('accounting').pipe(
      map((r) => {
        if (!r.fechaSistema) {
          throw new Error('No se encontró fecha del sistema en la respuesta');
        }
        return { fechaSistema: r.fechaSistema };
      }),
    );
  }

  getAccountingDate(): Observable<{ fechaSistema: string | null }> {
    return this.consultar('accounting');
  }

  getTreasuryDate(): Observable<{ fechaSistema: string | null }> {
    return this.consultar('treasury');
  }

  getParametrosForModule(module: ModuloParametros): Observable<{ fechaSistema: string | null }> {
    return this.consultar(module);
  }

  /**
   * Pide la fecha al modulo indicado.
   *
   * La ruta se resuelve en cada llamada y no se guarda en `baseUrl`: antes se
   * mutaba el estado del servicio y una peticion podia acabar consultando otro
   * modulo distinto del pedido.
   */
  private consultar(modulo: ModuloParametros): Observable<{ fechaSistema: string | null }> {
    return this.http.getContent<{ fechaSistema?: string }>(RUTA_FECHA[modulo]).pipe(
      timeout(10000),
      map((content) => ({ fechaSistema: content?.fechaSistema ?? null })),
    );
  }
}
